import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, Music2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const POLL_MS = 20000;

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const FanWall = () => {
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const fetchReactions = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/voting/reactions/wall`, {
          params: { limit: 30 },
        });
        if (!cancelled) setReactions(data.reactions || []);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    };
    fetchReactions();
    const t = setInterval(fetchReactions, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Auto-scroll animation - a slow continuous drift on the marquee track
  // (CSS handles the visual; we just duplicate content for seamless loop)

  if (loading || reactions.length === 0) return null;

  const duplicated = [...reactions, ...reactions]; // seamless loop

  return (
    <section className="py-14 relative overflow-hidden" data-testid="fan-wall-section">
      <div className="absolute inset-0 bg-gradient-to-b from-black via-purple-950/10 to-black" />
      <div className="relative z-10">
        <div className="container mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center shadow-lg shadow-pink-500/30">
              <MessageCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="text-xs uppercase tracking-widest text-pink-300 font-semibold">Fan Wall</span>
              <h2 className="text-2xl md:text-3xl font-bold leading-tight">What real fans are saying</h2>
            </div>
          </div>
        </div>

        {/* Marquee scroller - CSS keyframe animation */}
        <div
          className="relative fan-wall-scroller"
          data-testid="fan-wall-scroller"
          ref={scrollerRef}
        >
          <div className="fan-wall-track flex gap-4 px-6">
            {duplicated.map((r, i) => (
              <ReactionCard key={`${r.id}-${i}`} reaction={r} />
            ))}
          </div>
        </div>

        {/* Fade edges */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black to-transparent z-20" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-black to-transparent z-20" />
      </div>

      <style>{`
        .fan-wall-scroller { overflow: hidden; padding: 8px 0; mask-image: linear-gradient(90deg, transparent 0, black 5%, black 95%, transparent 100%); }
        .fan-wall-track { width: max-content; animation: fanwall-scroll 60s linear infinite; }
        .fan-wall-scroller:hover .fan-wall-track { animation-play-state: paused; }
        @keyframes fanwall-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .fan-wall-track { animation: none; }
        }
      `}</style>
    </section>
  );
};

const ReactionCard = ({ reaction }) => {
  const name = reaction.name || 'Anonymous';
  const initial = (name[0] || 'A').toUpperCase();
  return (
    <article
      className="glass-card border border-white/5 p-4 w-72 md:w-80 flex-shrink-0"
      data-testid={`fan-wall-card`}
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-xs">{initial}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-white truncate">{name}</p>
          <p className="text-[10px] uppercase tracking-widest text-gray-500">{timeAgo(reaction.created_at)}</p>
        </div>
      </div>

      <p className="text-sm text-gray-200 leading-snug mb-3 line-clamp-3 break-words">
        {reaction.comment}
      </p>

      {reaction.snippet_title && (
        <div className="flex items-center gap-1.5 text-[11px] text-cyan-300/80 truncate">
          <Music2 className="w-3 h-3 flex-shrink-0" />
          <span className="truncate">on <strong className="text-cyan-200">{reaction.snippet_title}</strong></span>
        </div>
      )}
    </article>
  );
};
